import {gql} from '@apollo/client';
import {groupBy, noop} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ProcedureRunPlanConditionREST, updateProcedureRunPlan} from 'procedures/network/procedureRunPlanDAO';
import {getDestinationUnitOpInstanceKey} from 'studies/sidepanel/AddProcedureRunModalUtils';
import AddPlanUnitOperationLinksModal from 'studies/typed/shared/AddPlanUnitOperationLinksModal';
import {UnitOpLinkEdge} from 'studies/typed/shared/unit-op-linking-flowchart/UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from 'studies/typed/shared/unit-op-linking-flowchart/useUnitOpLinkingState';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  LinkToExistingUnitOperationsPlanButtonQuery,
  LinkToExistingUnitOperationsPlanButtonQueryVariables,
} from './__generated/LinkToExistingUnitOperationsPlanButtonQuery';

export const LINK_TO_EXISTING_UNIT_OPERATIONS_PLAN_BUTTON_QUERY = gql`
  query LinkToExistingUnitOperationsPlanButtonQuery(
    $studyId: StrictString!
    $procedureRunPlanId: StrictString!
  ) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        procedureRunCount
      }
      ...InaccessibleInfoGraphQL
    }
    procedureRunPlan(id: $procedureRunPlanId) {
      ... on ProcedureRunPlanNode {
        id
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            nodes {
              id
              name
            }
            procedureFlowchart {
              ... on ProcedureFlowchartNode {
                id
                parent {
                  ... on ProcedureNode {
                    id
                    procedureType
                  }
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            nodes {
              id
              name
            }
            procedure {
              ... on ProcedureNode {
                id
                procedureType
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureRunPlanConditions {
          id
          name
          position
          templateMethodVersion {
            ... on ProcedureMethodDefinitionVersionNode {
              id
            }
            ... on InaccessibleInfoNode {
              ...InaccessibleInfoGraphQL
            }
          }
          methodParametersPlans {
            templateMethodVersion {
              id
            }
            dataParameterValues {
              parameterId
              value
            }
            materialInputValues {
              materialInputId
              value
            }
            equipmentValues {
              equipmentId
              value
            }
          }
          replicates {
            replicateNumber
            sourcePmeiIds
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type ProcedureRunPlanNode = ExtractByTypename<
  LinkToExistingUnitOperationsPlanButtonQuery['procedureRunPlan'],
  'ProcedureRunPlanNode'
>;
type FlowchartVersionNode = ExtractByTypename<
  NonNullable<ProcedureRunPlanNode['templateProcedureFlowchartVersion']>,
  'ProcedureFlowchartVersionNode'
>;
type ConfigVersionProxyNode = ExtractByTypename<
  NonNullable<ProcedureRunPlanNode['procedureFlowchartConfigVersionProxy']>,
  'ProcedureFlowchartConfigVersionProxyNode'
>;

interface LinkToExistingUnitOperationsPlanButtonProps {
  studyId: string;
  procedureRunPlanId: string;
  procedureRunPlanName: string;
  /** Callback when links are saved to the backend. Called after successful save to allow parent to refresh. */
  onLinksSaved: () => void;
}

/**
 * Transforms backend sourcePmeiIds format to frontend UnitOpLinkEdge format.
 * @param conditions - Plan conditions with replicates containing sourcePmeiIds
 * @param methods - Method nodes from the flowchart version
 * @returns Array of UnitOpLinkEdge representing the links
 */
function transformBackendLinksToEdges(
  conditions: ProcedureRunPlanNode['procedureRunPlanConditions'],
  methods: FlowchartVersionNode['nodes'] | ConfigVersionProxyNode['nodes']
): Array<UnitOpLinkEdge> {
  const edges: Array<UnitOpLinkEdge> = [];

  conditions.forEach((condition) => {
    const templateMethodVersion = condition.templateMethodVersion;
    // Template procedure accessibility should be checked before calling this function, since we need the
    // template procedure to get the methods
    assert(!isInaccessible(templateMethodVersion), 'Expected template method version to be accessible');
    const ownPmdvId = templateMethodVersion.id;
    const method = methods.find((m) => m.id === ownPmdvId);
    assert(method != null, `Expected to find method with id ${ownPmdvId} for plan condition`);

    condition.replicates.forEach((replicate) => {
      const sourcePmeiIds = replicate.sourcePmeiIds;
      if (sourcePmeiIds.length === 0) {
        return;
      }

      sourcePmeiIds.forEach((sourcePmeiId) => {
        // Create destination PMEI ID and add edge
        const destinationUnitOpInstanceKey = getDestinationUnitOpInstanceKey(
          method.name,
          condition.name,
          replicate.replicateNumber
        );
        edges.push({
          sourcePMEIId: sourcePmeiId,
          destinationPMEIId: destinationUnitOpInstanceKey,
        });
      });
    });
  });

  return edges;
}

/**
 * Transforms frontend UnitOpLinkEdge format to backend sourcePMEIIdsByTemplatePMDVId format.
 * @param links - Frontend links to transform
 * @param destinationPMEIs - Destination PMEIs with templateMethodId mapping
 * @param conditions - Plan conditions to update
 * @returns Conditions with updated replicateInfos containing sourcePMEIIdsByTemplatePMDVId
 */
function transformEdgesToBackendFormat(
  links: Array<UnitOpLinkEdge>,
  destinationPMEIs: Array<UnitOperationFields>,
  conditions: ProcedureRunPlanNode['procedureRunPlanConditions']
): Array<ProcedureRunPlanConditionREST> {
  // Build mapping from destination PMEI ID to its metadata
  const destPMEIMap = new Map<
    string,
    {templateMethodId: string; conditionName: string; replicateNumber: number}
  >();
  destinationPMEIs.forEach((pmei) => {
    destPMEIMap.set(pmei.pmeiId, {
      templateMethodId: pmei.templateMethodId,
      conditionName: pmei.conditionName,
      replicateNumber: pmei.replicateNumber,
    });
  });

  // Group links by condition and replicate
  const linksByConditionReplicate = new Map<string, Map<string, Array<string>>>();

  links.forEach((link) => {
    const destInfo = destPMEIMap.get(link.destinationPMEIId);
    assert(destInfo != null, `Expected to find destination PMEI info for ${link.destinationPMEIId}`);

    const key = `${destInfo.conditionName}-${destInfo.replicateNumber}`;
    let templateMap = linksByConditionReplicate.get(key);
    if (templateMap == null) {
      templateMap = new Map();
      linksByConditionReplicate.set(key, templateMap);
    }

    let sourceIds = templateMap.get(destInfo.templateMethodId);
    if (sourceIds == null) {
      sourceIds = [];
      templateMap.set(destInfo.templateMethodId, sourceIds);
    }
    sourceIds.push(link.sourcePMEIId);
  });

  // Build the conditions update payload
  return conditions.map((condition) => ({
    id: condition.id,
    name: condition.name,
    position: condition.position,
    methodPlans: condition.methodParametersPlans.map((plan) => ({
      methodVersionId: plan.templateMethodVersion.id,
      dataParameterValues: plan.dataParameterValues.map((pv) => ({
        dataParameterVersionId: pv.parameterId,
        values: pv.value != null ? [pv.value] : [],
      })),
      materialInputValues: plan.materialInputValues.map((mv) => ({
        materialInputVersionId: mv.materialInputId,
        values: mv.value != null ? [mv.value] : [],
      })),
      equipmentValues: plan.equipmentValues.map((ev) => ({
        equipmentVersionId: ev.equipmentId,
        values: ev.value != null ? [ev.value] : [],
      })),
    })),
    replicateInfos: condition.replicates.map((replicate) => {
      const key = `${condition.name}-${replicate.replicateNumber}`;
      const templateMap = linksByConditionReplicate.get(key);
      const ownPmdvId =
        condition.templateMethodVersion != null
          ? getAccessibleOrInaccessibleId(condition.templateMethodVersion)
          : null;

      const sourcePMEIIdsByTemplatePMDVId: Record<string, Array<string>> = {};
      if (templateMap != null) {
        templateMap.forEach((sourceIds, templateId) => {
          if (ownPmdvId != null && templateId !== ownPmdvId) {
            return;
          }
          sourcePMEIIdsByTemplatePMDVId[templateId] = sourceIds;
        });
      }

      return {
        replicateNumber: replicate.replicateNumber,
        sourcePMEIIdsByTemplatePMDVId,
      };
    }),
  }));
}

function hasUpdatedLinks(links: Array<UnitOpLinkEdge>, existingLinks: Array<UnitOpLinkEdge>): boolean {
  const getLinkKey = (link: UnitOpLinkEdge): string => `${link.sourcePMEIId}->${link.destinationPMEIId}`;
  const newLinkKeys = new Set(links.map((link) => getLinkKey(link)));
  const existingLinkKeys = new Set(existingLinks.map((link) => getLinkKey(link)));

  if (newLinkKeys.size !== existingLinkKeys.size) {
    return true;
  }

  return [...newLinkKeys].some((key) => !existingLinkKeys.has(key));
}

function getDestinationPMEIsFromPlan(
  methods: FlowchartVersionNode['nodes'] | ConfigVersionProxyNode['nodes'],
  conditions: ProcedureRunPlanNode['procedureRunPlanConditions'],
  procedureId: string,
  procedureType: ProcedureType,
  procedureRunPlanName: string
): Array<UnitOperationFields> {
  if (conditions.length === 0) {
    return [];
  }
  const sortedConditions = [...conditions].sort((a, b) => a.position - b.position);
  const conditionsByPosition = groupBy(sortedConditions, (condition) => condition.position);
  // We currently enforce that condition metadata (name, description, replicate count) is uniform within a
  // "condition group", consisting of all the the conditions at the same position
  const canonicalConditions = Object.keys(conditionsByPosition)
    .map((position) => Number(position))
    .sort((a, b) => a - b)
    .map((position) => conditionsByPosition[position][0]);

  return methods.flatMap((method, methodIndex) =>
    canonicalConditions.flatMap((condition, conditionIndex) =>
      condition.replicates.map((replicate) => ({
        pmeiId: getDestinationUnitOpInstanceKey(method.name, condition.name, replicate.replicateNumber),
        // Note: when isPlan is true, procedureRunName holds the name of the procedure run plan.
        procedureRunName: procedureRunPlanName,
        procedureRunId: procedureId,
        isPlan: true,
        procedureRunDisplayId: null,
        procedureRunOrderKey: 0,
        methodName: method.name,
        templateMethodId: method.id,
        methodOrderKey: methodIndex,
        conditionName: condition.name,
        conditionOrderKey: conditionIndex,
        replicateNumber: replicate.replicateNumber,
        procedureType,
        status: null,
      }))
    )
  );
}

export default function LinkToExistingUnitOperationsPlanButton(
  props: LinkToExistingUnitOperationsPlanButtonProps
): JSX.Element {
  const {studyId, procedureRunPlanId, procedureRunPlanName, onLinksSaved} = props;

  const result = useBenchlingQuery<
    LinkToExistingUnitOperationsPlanButtonQuery,
    LinkToExistingUnitOperationsPlanButtonQueryVariables
  >(LINK_TO_EXISTING_UNIT_OPERATIONS_PLAN_BUTTON_QUERY, {
    variables: {studyId, procedureRunPlanId},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    renderCachedDataWhenRefetching: true,
  });

  // Extract methods and plan data for link transformations
  const {destinationPMEIs, methods, plan} = useMemo(() => {
    if (result.type !== QueryResultType.SUCCESS) {
      return {destinationPMEIs: [], methods: [], plan: null};
    }
    if (isInaccessible(result.data.procedureRunPlan)) {
      return {destinationPMEIs: [], methods: [], plan: null};
    }

    const planData = result.data.procedureRunPlan;
    const flowchartVersion = planData.procedureFlowchartConfigVersionProxy;
    const legacyFlowchartVersion = planData.templateProcedureFlowchartVersion;

    if (flowchartVersion != null && !isInaccessible(flowchartVersion)) {
      const proxy = flowchartVersion;
      return {
        destinationPMEIs: getDestinationPMEIsFromPlan(
          proxy.nodes,
          planData.procedureRunPlanConditions,
          proxy.procedure.id,
          proxy.procedure.procedureType,
          procedureRunPlanName
        ),
        methods: proxy.nodes,
        plan: planData,
      };
    } else if (legacyFlowchartVersion != null && !isInaccessible(legacyFlowchartVersion)) {
      const version = legacyFlowchartVersion;
      if (version.procedureFlowchart.parent == null) {
        return {destinationPMEIs: [], methods: [], plan: null};
      }
      return {
        destinationPMEIs: getDestinationPMEIsFromPlan(
          version.nodes,
          planData.procedureRunPlanConditions,
          version.procedureFlowchart.parent.id,
          version.procedureFlowchart.parent.procedureType,
          procedureRunPlanName
        ),
        methods: version.nodes,
        plan: planData,
      };
    }
    return {destinationPMEIs: [], methods: [], plan: null};
  }, [procedureRunPlanName, result]);

  // Load existing links from the plan's persisted data
  const existingLinks = useMemo(() => {
    if (plan == null || methods.length === 0) {
      return [];
    }
    return transformBackendLinksToEdges(plan.procedureRunPlanConditions, methods);
  }, [plan, methods]);

  let disabled = true;
  let tooltipText: string | null = null;
  let onClickHandler: () => void = noop;

  if (result.type === QueryResultType.PENDING) {
    tooltipText = 'Loading...';
  } else if (result.type === QueryResultType.SUCCESS) {
    if (!isInaccessible(result.data.study) && !isInaccessible(result.data.procedureRunPlan)) {
      const study = result.data.study;
      const hasProcedureRuns = study.procedureRunCount > 0;
      disabled = !hasProcedureRuns;

      if (!hasProcedureRuns) {
        tooltipText = 'No existing recipe runs to link to';
      }
    } else {
      tooltipText = 'Unable to access study or procedure run plan';
    }
  } else {
    tooltipText = 'Failed to load data';
  }

  assert(procedureRunPlanId != null, 'Procedure Run Plan is not selected');
  assert(procedureRunPlanName != null, 'Procedure Run Plan is not selected');

  if (!disabled && plan != null) {
    onClickHandler = async () => {
      const newLinks = await legacyRunModal(AddPlanUnitOperationLinksModal, {
        studyId,
        procedureRunPlanName,
        destinationPMEIs,
        initialLinks: existingLinks,
      });

      // Save links to the backend if user confirmed (newLinks is not null)
      if (newLinks != null) {
        const linksWereUpdated = hasUpdatedLinks(newLinks, existingLinks);
        const conditionsUpdate = transformEdgesToBackendFormat(
          newLinks,
          destinationPMEIs,
          plan.procedureRunPlanConditions
        );

        await updateProcedureRunPlan({
          id: procedureRunPlanId,
          studyId,
          experimentConditions: conditionsUpdate,
        });

        // Notify parent that links were saved (for refetching if needed)
        onLinksSaved();
        if (linksWereUpdated) {
          notifications.success('Updated planned unit op links successfully');
        }

        // Refetch the query to get updated links (fire-and-forget, don't block the callback)
        result.refetch();
      }
    };
  }

  return (
    <Tooltip isDisabled={!disabled} text={tooltipText}>
      <Button
        dataTestAttrs={{component: 'LinkToExistingPlanButton', element: 'button'}}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        onClick={onClickHandler}
        text="Link unit operations"
        icon="diagram-nested"
        disabled={disabled}
      />
    </Tooltip>
  );
}
